import React from "react"
import type { FC } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { AppSidebar } from "@/features/admin/components/common/layout/app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb"
import { Separator } from "@/shared/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/shared/components/ui/sidebar"

const AdminLayout: FC = () => {
  const location = useLocation()
  

  // Generate breadcrumbs based on current path
  const getBreadcrumbs = () => {
    const path = location.pathname
    
    if (path === '/admin' || path === '/admin/') {
      return [{ name: 'Dashboard', href: '/admin' }]
    }
    
    if (path.startsWith('/admin/packages')) {
      const breadcrumbs = [
        { name: 'Dashboard', href: '/admin' },
        { name: 'Packages', href: '/admin/packages' }
      ]
      
      if (path.includes('/archived')) {
        breadcrumbs.push({ name: 'Archived', href: '/admin/packages/archived' })
      } else if (path.includes('/expired')) {
        breadcrumbs.push({ name: 'Expired', href: '/admin/packages/expired' })
      } else if (path.includes('/deleted')) {
        breadcrumbs.push({ name: 'Deleted', href: '/admin/packages/deleted' })
      }
      
      return breadcrumbs
    }
    
    if (path.startsWith('/admin/inbox')) {
      const breadcrumbs = [
        { name: 'Dashboard', href: '/admin' },
        { name: 'Inbox', href: '/admin/inbox/messages' }
      ]
      
      if (path.includes('/messages')) {
        breadcrumbs.push({ name: 'Messages', href: '/admin/inbox/messages' })
      } else if (path.includes('/visa-applications')) {
        breadcrumbs.push({ name: 'Visa Applications', href: '/admin/inbox/visa-applications' })
      } else if (path.includes('/travel-insurance')) {
        breadcrumbs.push({ name: 'Travel Insurance', href: '/admin/inbox/travel-insurance' })
      }
      
      return breadcrumbs
    }
    
    if (path === '/admin/users') {
      return [
        { name: 'Dashboard', href: '/admin' },
        { name: 'Users', href: '/admin/users' }
      ]
    }
    
    if (path === '/admin/settings') {
      return [
        { name: 'Dashboard', href: '/admin' },
        { name: 'Settings', href: '/admin/settings' }
      ]
    }
    
    return [{ name: 'Dashboard', href: '/admin' }]
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <div className="admin-layout" data-admin="true">
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={`${breadcrumb.href}-${index}`}>
                    <BreadcrumbItem>
                      {index === breadcrumbs.length - 1 ? (
                        <BreadcrumbPage>
                          {breadcrumb.name}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink href={breadcrumb.href}>
                          {breadcrumb.name}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && (
                      <BreadcrumbSeparator />
                    )}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          
          <main className="flex flex-1 flex-col gap-4 p-4">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}

export default AdminLayout
